// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CopyReportAction.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { compose, withHandlers, withState, setDisplayName, mapProps } from 'recompose';
import { toJS } from 'mobx';
import constants from 'logic/data/Izenda.ReportsConstants';
import CategoryDetail from 'logic/models/Izenda.CategoryDetail';
import uiState from 'logic/Izenda.UiState';
import { curry, isNil, assign, find, pick, isEmpty, isArray, concat, cloneDeep } from 'lodash/fp';

const CopyReportAction = compose(
  setDisplayName('CopyReport'),
  withState('reportName', 'handleChangeReportName', props => props.boardDetail.name),
  withState('category', 'changeCategory', () => null),
  withState('saveIntos', 'updateSaveIntos', () => []),
  withState('subCategory', 'changeSubCategory', () => null),
  withState('subCategories', 'updateSubCategories', () => []),
  withState('categories', 'updateCategories', () => []),
  withState('saveInto', 'updateSaveInto', props =>
    // convert isGlobal to 0 and 1 for dropdown list, if tenant user default value is 0
    isNil(uiState.tenantId) ? +props.boardDetail.isGlobal : 0
  ),

  mapProps(props => {
    const localization = props.localization;
    const typeName =
      props.boardDetail.type == constants.CATEGORY_TYPE.REPORT
        ? localization.getText('COMMON_LABEL_REPORT')
        : localization.getText('COMMON_LABEL_TEMPLATE');
    const hiddenSaveintos = uiState.currentUser.systemAdmin && isNil(uiState.tenantId);

    return assign(props, { typeName: typeName, hiddenSaveintos: hiddenSaveintos });
  }),
  withHandlers({
    handleValidate: curry(handleValidate),
    updateCategory: curry(updateCategory),
    hasNewCategory: curry(hasNewCategory),
    addNewCategory: curry(addNewCategory),
    updateSubCategory: curry(updateSubCategory),
    addNewSubCategory: curry(addNewSubCategory),
    onClosing: curry(onClosing),
    onChangeSaveInto: curry(onChangeSaveInto)
  })
);

/**
 * Detect that user enter a new category or new subcategory
 * @param {Object} props components property
 */
function hasNewCategory(props: any) {
  const { categories, subCategories } = props;
  const hasNewCat = find(['isNew', true], categories);
  const hasNewSubCat = find(['isNew', true], subCategories);
  return !isNil(hasNewCat) || !isNil(hasNewSubCat);
}

/**
 * Handle validate when save or copy report/template
 * @param   {Object}  props components property
 * @return  {Promise}
 */
function handleValidate(props: any) {
  const {
    store,
    loader,
    validation: valManager,
    boardDetail: { type },
    isMoveReport,
    reportName,
    category,
    subCategory,
    boardDetail,
    version
  } = props;
  const contentType = type === Number(constants.CATEGORY_TYPE.REPORT) ? 'report' : 'template';

  const formatMessage = isMoveReport ? constants.MESSAGES.M06b : constants.MESSAGES.M06a;

  const successMessage = String.format(
    formatMessage,
    contentType,
    reportName,
    !isEmpty(category) ? category.name : constants.UNCATEGORIZED,
    !isEmpty(subCategory) ? '/' + subCategory.name : ''
  );

  const dataObject = pick(['reportName', 'category', 'subCategory', 'saveInto'], props);

  return valManager.validate().then(success => {
    if (success) {
      loader.startLoading();
      return store
        .copyNewReport(dataObject, boardDetail, isMoveReport, version)
        .then(response => {
          if (isArray(response)) {
            valManager.updateErrors(response);
            if (isNil(response[0].key) || isEmpty(response[0].key)) {
              props.confirmMessage('info', response[0].messages);
            }
            return false;
          }

          return response;
        })
        .then(result => {
          store.model.hasNewCategory = hasNewCategory(props);
          return result && props.onAccept(successMessage, result, dataObject);
        })
        .finally(loader.endLoading.bind(loader));
    }
  });
}

/**
 * Add new category
 * @param {Object} props Component props
 * @param {Object} item  New category item added
 */
function addNewCategory(props: any, item: any) {
  props.updateCategories(concat(item, props.categories));
}

/**
 * Update selected category
 * @param {Object} props Component props
 * @param {String} item  category id
 * @param {String} name  category name
 * @param {Bool}   isNew
 */
function updateCategory(props: any, item: string, name: string, isNew: boolean) {
  // update selected category
  props.changeCategory({ id: item, name: name });

  // reset selected sub category
  props.changeSubCategory(null);

  // reload sub category list if have category id another case set subcategories to empty array
  if (!isNew) {
    props.store.loadSubCategoriesForCopyPopup(item).then(subCategories => {
      subCategories &&
        props.updateSubCategories(
          subCategories.map(function(cate) {
            return new CategoryDetail(cate);
          })
        );
    });
  } else {
    props.updateSubCategories([]);
  }
}

/**
 * Add new sub category
 * @param {Object} props Components props
 * @param {Object} item  New sub category
 */
function addNewSubCategory(props: any, item: any) {
  props.updateSubCategories(concat(item, props.subCategories));
}

/**
 * Update selected  sub category
 * @param {Object} props Component props
 * @param {String} item  category id
 * @param {String} name  category name
 */
function updateSubCategory(props: any, item: string, name: string) {
  props.changeSubCategory({ id: item, name: name });
}

/** Onclosing popup */
function onClosing(props: any, callBack: any, type: any) {
  if (type === 'accept') {
    return handleValidate(props);
  } else {
    props.onReject();
  }
}

/**
 * Change save into dropdown
 * @param {Object} props  Component property
 * @param {Object} item   Selected item.
 */
function onChangeSaveInto(props: any, item: any) {
  props.updateSaveInto(item.value);
  props.changeCategory(null);
  props.changeSubCategory(null);
  const { store } = props;
  props.store.loadCategoriesForCopyPopup(store.reportType, item.value).then(() => {
    props.updateCategories(cloneDeep(toJS(store.model.copyReportCategoryList)));
  });
}

export default CopyReportAction;



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportList/CopyReport/Izenda.CopyReportAction.js